import { CommonModule } from '@angular/common';
import { Component, computed, inject, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MoneyTrackerSetup } from '@app/_interfaces/money-tracker.interface';
import { AlertService } from '@app/_services/alert.service';
import { CacheService } from '@app/_services/cache.service';
import { DependencyService } from '@app/_services/dependency.service';
import { SettingsService } from '@app/_services/settings.service';
import { UtilsService } from '@app/_services/utils.service';
import { MoneyTrackerStore } from '@app/_stores';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { MoneyTrackerSetupFormComponent } from '../money-tracker-setup-form/money-tracker-setup-form.component';
import { MoneyTransactionEntitiesComponent } from '../money-transaction-entities/money-transaction-entities.component';
import { TransactionType } from '@app/_types/generic.type';
import { ParseMoneyPipe } from '@app/_pipes/parse-money.pipe';
import { MoneyTransactionFormComponent } from '../money-transaction-form/money-transaction-form.component';
import { LastUpdatedByService } from '@app/_services/last-updated-by.service';
import { ModalService } from '@app/_services/modal.service';

export interface DeletedMoneyEntity {
  entityId: string;
  entityType: 'SETUP' | 'TRANSACTION';
  deletedAt: Date;
  entries: string[];
  title: string;
  setupId: string;
  currencyCode: string;
  amount: string | number | null;
  transactionType: TransactionType | null;
  attachmentCount: number;
}

@Component({
  selector: 'app-money-tracker-trash-tab',
  imports: [
    CommonModule,
    SvgComponent,
    ParseMoneyPipe
  ],
  templateUrl: './money-tracker-trash-tab.component.html',
  styleUrl: './money-tracker-trash-tab.component.scss'
})

export class MoneyTrackerTrashTabComponent {

  readonly moneyTrackerStore = inject(MoneyTrackerStore);
  readonly lastUpdatedByService = inject(LastUpdatedByService);
  deletedEntities: Signal<DeletedMoneyEntity[]> = computed(() => {
    const deletedSetupEntities: DeletedMoneyEntity[] = [];
    const deletedTransactionEntities: DeletedMoneyEntity[] = [];

    this.moneyTrackerStore.setups().forEach(moneyTracker => {
      if (moneyTracker.deletedAt && !moneyTracker.permaDeletedAt && moneyTracker.uid === this.cc.user.uid) {
        // Only count non-deleted transactions (matches Flutter behavior)
        const transactions = this.moneyTrackerStore.transactions().filter(
          transaction => transaction.setupId === moneyTracker.id && !transaction.deletedAt
        );
        deletedSetupEntities.push({
          entityId: moneyTracker.id,
          deletedAt: this.utils.toDate(moneyTracker.deletedAt),
          entries: transactions.map(transaction => transaction.id),
          title: moneyTracker.title,
          setupId: moneyTracker.id,
          currencyCode: moneyTracker.currencyCode,
          amount: null,
          transactionType: null,
          entityType: 'SETUP',
          attachmentCount: 0
        });
      }
    });

    this.moneyTrackerStore.transactions().forEach(transaction => {
      if (transaction.deletedAt && !transaction.permaDeletedAt && transaction.uid === this.cc.user.uid) {
        const setup = this.moneyTrackerStore.idToSetup()[transaction.setupId];
        if (!setup) return;
        deletedTransactionEntities.push({
          entityId: transaction.id,
          deletedAt: this.utils.toDate(transaction.deletedAt),
          entries: [],
          title: transaction.title,
          setupId: transaction.setupId,
          currencyCode: setup.currencyCode,
          amount: transaction.amount,
          transactionType: transaction.transactionType,
          entityType: 'TRANSACTION',
          attachmentCount: Object.keys(transaction.attachments).length
        });
      }
    });

    const deletedEntities = [...deletedSetupEntities, ...deletedTransactionEntities];

    deletedEntities.sort((a, b) => {
      return b.deletedAt.getTime() - a.deletedAt.getTime();
    });
    return deletedEntities;
  });

  idToSetup: Signal<Record<string, MoneyTrackerSetup>> = this.moneyTrackerStore.idToSetup;

  constructor(
    public ss: SettingsService,
    private alertService: AlertService,
    public dialog: MatDialog,
    public cc: CacheService,
    public ds: DependencyService,
    private utils: UtilsService,
    private modalService: ModalService
  ) { }

  trackByEntity(index: number, entity: DeletedMoneyEntity) {
    return entity.entityId;
  };

  openSetup(entityId: string) {
    const setup = this.idToSetup()[entityId];

    const moneyData = {
      mode: 'edit',
      value: setup,
    }

    if (this.cc.isNarrowScreen()) {
      const setupDialog = this.modalService.openMainContent(
        MoneyTrackerSetupFormComponent,
        moneyData,
        { disableClose: true, autoFocus: false }
      );
    } else {
      const setupDialog = this.modalService.openLevel1(
        MoneyTrackerSetupFormComponent,
        moneyData,
        { disableClose: true, autoFocus: false }
      );
    }
  };

  openTransaction(entityId: string) {
    const transaction = this.moneyTrackerStore.idToTransaction()[entityId];
    this.modalService.openLevel1(
      MoneyTransactionFormComponent,
      {
        mode: 'edit',
        value: transaction,
        transactionType: transaction.transactionType
      },
      { disableClose: false, autoFocus: false }
    );
  };

  openEntity(entity: DeletedMoneyEntity) {
    if (entity.entityType === 'SETUP') {
      this.openSetup(entity.entityId);
    } else if (entity.entityType === 'TRANSACTION') {
      this.openTransaction(entity.entityId);
    }
  }

  openEntities(entityId: string, event: MouseEvent) {
    event.stopPropagation();
    this.modalService.openLevel2(
      MoneyTransactionEntitiesComponent,
      { entityId: entityId },
      { disableClose: false, autoFocus: false }
    );
  };

  async remove(entityId: string, entityType: 'SETUP' | 'TRANSACTION') {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('removeConfirmation_title'),
      description: this.ss.t('removeConfirmation_content'),
      yesButtonText: this.ss.t('common_buttonRemove'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-11',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    if (entityType === 'TRANSACTION') {
      const transaction = this.moneyTrackerStore.transactions().find(transaction => transaction.id === entityId);
      if (!transaction) return;
      transaction.permaDeletedAt = new Date();
      this.moneyTrackerStore.updateMoneyTransactions([transaction]);
      return;
    } else if (entityType === 'SETUP') {
      const setup = this.idToSetup()[entityId];
      setup.permaDeletedAt = new Date();
      this.moneyTrackerStore.updateMoneySetups([setup]);
    }
  };

  async restore(entityId: string, entityType: 'SETUP' | 'TRANSACTION', setupId: string) {
    //check if the feature is disabled
    const isMoneyTrackerEnabled = this.moneyTrackerStore.disabledSetups();
    const isSetupDisabled = isMoneyTrackerEnabled.find(setup => setup.id === setupId);
    if (isSetupDisabled) {
      await this.alertService.alert(this.ss.t('featureTurnedOff_title'), this.ss.t('featureTurnedOff_content'), this.ss.t('common_ok'));
    }

    // Check if restoring a transaction whose setup is also deleted (in trash)
    if (entityType === 'TRANSACTION') {
      const setup = this.idToSetup()[setupId];
      if (setup?.deletedAt && !setup?.permaDeletedAt) {
        // Show special dialog with checkbox for restoring transaction with deleted setup
        await this.restoreTransactionWithDeletedSetup(entityId, setupId, isSetupDisabled);
        return;
      }
    }

    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('restoreConfirmation_title'),
      description: this.ss.t('restoreConfirmation_content'),
      yesButtonText: this.ss.t('common_restore'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-35',
      noButtonColor: 'color-7',
    });
    if (!res) return;
    if (entityType === 'TRANSACTION') {
      const transaction = this.moneyTrackerStore.transactions().find(transaction => transaction.id === entityId);
      if (!transaction) return;
      transaction.deletedAt = null;
      this.moneyTrackerStore.updateMoneyTransactions([transaction]);
      const isShow = await this.alertService.toast('moneyTrackerTransactionRestored_content', !isSetupDisabled ? 'common_show' : '', 'common_dismiss');
      if (isShow) {
        this.openTransaction(entityId);
      }
      return;
    } else if (entityType === 'SETUP') {
      const setup = this.idToSetup()[entityId];
      setup.deletedAt = null;
      this.moneyTrackerStore.updateMoneySetups([setup]);
      const isShow = await this.alertService.toast('moneyTrackerSetupRestored_content', !isSetupDisabled ? 'common_show' : '', 'common_dismiss');
      if (isShow) {
        this.openSetup(entityId);
      }
    }
  };

  /**
   * Special dialog for restoring a transaction when its setup is also in trash.
   * Shows a dialog with checkbox to optionally restore all deleted transactions of the setup.
   * Matches Flutter's behavior from entity_action_handler.dart
   */
  private async restoreTransactionWithDeletedSetup(transactionId: string, setupId: string, isSetupDisabled: MoneyTrackerSetup | undefined): Promise<void> {
    const res = await this.alertService.confirmRevamped({
      title: this.ss.t('restoreEntry_title'),
      description: this.ss.t('restoreEntry_content'),
      yesButtonText: this.ss.t('common_restore'),
      noButtonText: this.ss.t('common_buttonCancel'),
      yesButtonColor: 'color-35',
      noButtonColor: 'color-7',
      showCheckbox: true,
      checkboxChecked: false,
      checkboxLabel: this.ss.t('restoreEntry_subcontent'),
    });

    if (!res) return;

    // Check if user wants to restore all associated entries (checkbox checked)
    const restoreAllEntries = res === 'ADDITIONAL_ACTION';

    // 1. Restore the setup first
    const setup = this.idToSetup()[setupId];
    if (setup) {
      setup.deletedAt = null;
      await this.moneyTrackerStore.updateMoneySetups([setup]);
    }

    // 2. Restore the clicked transaction
    const transaction = this.moneyTrackerStore.transactions().find(t => t.id === transactionId);
    if (transaction) {
      transaction.deletedAt = null;
      await this.moneyTrackerStore.updateMoneyTransactions([transaction]);
    }

    // 3. If checkbox checked, restore ALL deleted transactions of this setup
    if (restoreAllEntries) {
      const deletedTransactions = this.moneyTrackerStore.transactions().filter(
        t => t.setupId === setupId && t.deletedAt && !t.permaDeletedAt && t.id !== transactionId
      );
      for (const t of deletedTransactions) {
        t.deletedAt = null;
      }
      if (deletedTransactions.length > 0) {
        await this.moneyTrackerStore.updateMoneyTransactions(deletedTransactions);
      }
    }

    // Show success toast
    const isShow = await this.alertService.toast('moneyTrackerTransactionRestored_content', !isSetupDisabled ? 'common_show' : '', 'common_dismiss');
    if (isShow) {
      this.openTransaction(transactionId);
    }
  }
}
